import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { BrandLogo } from '../components/BrandLogo';

export const LoginPage: React.FC = () => {
  const { signInGoogle, signInEmail, signUpEmail, isDevAuth, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<'in' | 'up'>('in');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const next = params.get('next') || '/app';

  useEffect(() => {
    if (user && !isDevAuth) navigate(next);
  }, [user, isDevAuth, navigate, next]);

  if (isDevAuth) {
    return (
      <main className="kin-page kin-auth-page">
        <BrandLogo size="xl" />
        <h1>Dev mode</h1>
        <p>Firebase Auth is bypassed locally. You are signed in as the Kinvera dev user.</p>
        <Link to="/app" className="kin-btn kin-btn-primary">
          Continue to studio
        </Link>
      </main>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'in') await signInEmail(email, password);
      else await signUpEmail(email, password);
      navigate(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Auth failed');
    } finally {
      setBusy(false);
    }
  };

  const google = async () => {
    setError(null);
    setBusy(true);
    try {
      await signInGoogle();
      navigate(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="kin-page kin-auth-page">
      <div className="kin-auth-card">
        <BrandLogo size="xl" />
        <h1>Sign in</h1>
        <p className="kin-section-lede">
          Restore damaged family photos while preserving the people in them.
        </p>
        {error && <p className="kin-banner kin-banner-error">{error}</p>}

        <button
          type="button"
          className="kin-btn kin-btn-primary kin-btn-google"
          onClick={google}
          disabled={busy}
        >
          Continue with Google
        </button>

        <button
          type="button"
          className="kin-linkish"
          onClick={() => setShowEmail((v) => !v)}
        >
          {showEmail ? 'Hide email sign-in' : 'Use email instead'}
        </button>

        {showEmail && (
          <>
            <form className="kin-form" onSubmit={submit}>
              <label>
                Email
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </label>
              <label>
                Password
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  autoComplete={mode === 'in' ? 'current-password' : 'new-password'}
                />
              </label>
              <button type="submit" className="kin-btn kin-btn-ghost" disabled={busy}>
                {mode === 'in' ? 'Sign in with email' : 'Create account'}
              </button>
            </form>
            <button
              type="button"
              className="kin-linkish"
              onClick={() => setMode((m) => (m === 'in' ? 'up' : 'in'))}
            >
              {mode === 'in' ? 'Need an account? Sign up' : 'Have an account? Sign in'}
            </button>
          </>
        )}
      </div>
    </main>
  );
};

export const BillingSuccessPage: React.FC = () => (
  <main className="kin-page">
    <h1>Credits added</h1>
    <p>Your restoration credits are ready to use.</p>
    <Link to="/app" className="kin-btn kin-btn-primary">
      Restore a photo
    </Link>
  </main>
);
